/**
 * Configuration of the Design Builder's BUTTON widget.
 *
 * A button has exactly ONE feature (`action`), picked from a dropdown in the
 * inspector; only that feature's settings are shown and used. Everything is
 * design tier — stored on `widget.appearance` — so it survives publishing
 * (publishing strips `widget.data` row keys, never `appearance`).
 *
 * Key names deliberately avoid the publish-stripped row keys (`value`,
 * `options`, `rows`, `data`, …), hence `filterValue` rather than `value`.
 */

import { EXCEL_EXPORT_DEFAULT_BODY, EXCEL_EXPORT_DEFAULT_URL } from '../../self-service-visualization/shared/platform-excel-export.utils';

export type ButtonAction = 'none' | 'download' | 'api' | 'filter' | 'navigate' | 'popup' | 'closePopup' | 'approval' | 'export' | 'saveTable' | 'upload';

export type ButtonHttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** One API the button calls — the "add API" list, like the table's action APIs. */
export interface ButtonApiCall {
  id: string;
  name: string;
  method: ButtonHttpMethod;
  url: string;
  /** JSON body with `{componentId}` / `{label}` / `{timestamp}` tokens. */
  body: string;
  /** Optional JSON object of extra request headers. */
  headers: string;
}

/** A column a "Save changes" (values) button updates, and the widget (node id) giving its value. */
export interface ButtonSaveColumn {
  column: string;
  sourceId: string;
}

/** excel = the platform's Excel export of a dataset, filtered by the dashboard's selections. */
export type ButtonDownloadSource = 'api' | 'widget' | 'excel';
export type ButtonDownloadFormat = 'csv' | 'json';
export type ButtonNavigateType = 'landing' | 'dashboard' | 'screen' | 'route' | 'url';
/** widget = a Popup widget; inline = title + message; screen = a frame designed on the canvas. */
export type ButtonPopupSource = 'widget' | 'inline' | 'screen';
export type ButtonPopupSize = 'small' | 'medium' | 'large' | 'square';

export interface ButtonConfig {
  action: ButtonAction;

  // ── Label & style ──────────────────────────────────────────────────────────
  label: string;
  icon: string;
  iconPosition: 'left' | 'right';
  labelAlign: 'center' | 'left' | 'right';
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  textColor: string;
  backgroundColor: string;
  hoverBackgroundColor: string;
  borderColor: string;
  borderWidth: number;
  borderRadius: number;
  fullWidth: boolean;
  /** Shown in a confirm() before the action runs; empty = no confirmation. */
  confirmMessage: string;

  // ── Download ───────────────────────────────────────────────────────────────
  downloadSource: ButtonDownloadSource;
  downloadUrl: string;
  downloadMethod: ButtonHttpMethod;
  downloadBody: string;
  /** Node id of the widget whose data is exported (source = widget). */
  downloadWidgetId: string;
  downloadFormat: ButtonDownloadFormat;
  downloadFileName: string;
  /** Excel export: the dataset (entity) exported; blank = the dataset of `downloadWidgetId`. */
  downloadEntity: string;
  /** Excel export: the sheet's name. */
  downloadSheetName: string;

  // ── Call API ───────────────────────────────────────────────────────────────
  apis: ButtonApiCall[];
  apiRunMode: 'sequential' | 'parallel';
  /** componentIds refreshed from a `{ results: [...] }` response. */
  apiTargets: string[];
  apiSuccessMessage: string;

  // ── Approval ───────────────────────────────────────────────────────────────
  /** The approval API, e.g. /approval-level/aw_oem_koms_pricing_approval/submit. */
  approvalUrl: string;
  /** Where the values come from: a zone slider (its handle value and record id) or a
   *  table (one item per row); empty = the first zone slider on the page. */
  approvalSliderId: string;
  /** Table source: the column holding each row's record id - may be a hidden column. */
  approvalUuidColumn: string;
  /** Table source: the column whose value is submitted for each row. */
  approvalValueColumn: string;
  /** Table source: only the rows the user edited, or every row the table shows. */
  approvalRows: 'edited' | 'all';

  // ── Save changes ───────────────────────────────────────────────────────────
  /** table = a table's edited rows (its own row Save); values = the dashboard's values, as an input box's Save. */
  saveSource: 'table' | 'values';
  /** The table whose edited rows the button saves, with the table's own row Save
   *  (its Save API, business keys, mandatory columns); empty = the first table on the screen. */
  saveTableId: string;
  /** Values: the update API, its entity and the business keys (values from the current selection / the clicked row). */
  saveUrl: string;
  saveEntity: string;
  saveBusinessKeys: string[];
  /** Values: each column updated, with the dashboard widget its value comes from ('' = the one for that column). */
  saveColumns: ButtonSaveColumn[];
  saveSuccessMessage: string;
  /** Request body; tokens {recordId} {value} {userEmail} (+ the usual button tokens). */
  approvalBody: string;
  approvalSuccessMessage: string;
  /** Reload the dashboard's data after a successful approval. */
  approvalRefresh: boolean;
  /** Optional: after the approval API answers OK, navigate with the
   *  navigate* settings below (screen, dashboard, route, URL, landing). */
  approvalNavigate: boolean;

  // ── Upload ─────────────────────────────────────────────────────────────────
  /** The API the picked file is POSTed to; blank = the platform API. */
  uploadUrl: string;
  /** json = a JSON body (uploadBody) with the file as base64; multipart = form-data. */
  uploadMode: 'json' | 'multipart';
  /** JSON mode: the dataset the file is uploaded to ({entity}). */
  uploadEntity: string;
  /** JSON mode: business-key columns of that dataset; each value comes from the
   *  clicked table row, else the dashboard selection ({businessKeys}). */
  uploadKeyColumns: string[];
  /** JSON mode: the request body, written by the designer. {fileBytes} = the file's
   *  bytes, {fileBase64}, {fileName} {fileType} {contentType} {fileSize} {userEmail}, and every
   *  dashboard selection / clicked table row column by its name. A body with
   *  `requestData` gets the platform envelope it lacks (see UPLOAD_ENVELOPE). */
  uploadBody: string;
  /** Form field name the file is sent under. */
  uploadFieldName: string;
  /** File types the picker offers, e.g. ".xlsx,.csv"; empty = any file. */
  uploadAccept: string;
  /** Largest file allowed in MB; 0 = no limit. */
  uploadMaxSizeMb: number;
  /** Optional JSON object of extra form fields ({userEmail} {fileName} {componentId} tokens). */
  uploadExtraFields: string;
  uploadSuccessMessage: string;
  /** Reload the dashboard's data after a successful upload (off by default: an
   *  attachment upload changes nothing the dashboard shows). */
  uploadRefresh: boolean;

  // ── Export (the platform's Excel export, as a nested table's Export button) ──
  /** Node id of the component exported: its dataset is the entity, its rows feed the filter values. */
  exportWidgetId: string;
  /** The export API; blank = the platform API. */
  exportUrl: string;
  /** Body template: {entity} {filters} {fileName} {sheetName} {userEmail}. */
  exportBody: string;
  /** Dataset columns sent as filters (the component's rows, else the dashboard selection); empty = every selection. */
  exportFilterColumns: string[];
  exportSheetName: string;
  /** Blank = the dataset's name. */
  exportFileName: string;

  // ── Filter ─────────────────────────────────────────────────────────────────
  /** componentIds the filter recomputes. */
  filterTargets: string[];
  /** Field / column the filter applies to. */
  filterField: string;
  /** Target value(s); comma-separated for several. */
  filterValue: string;
  /** Second click clears the filter again. */
  filterToggle: boolean;
  /** Optional custom endpoint; empty = the dashboard's compute API. */
  filterApiUrl: string;
  filterApiMethod: 'GET' | 'POST' | 'PUT' | 'PATCH';
  filterApiBody: string;

  // ── Navigate ───────────────────────────────────────────────────────────────
  navigateType: ButtonNavigateType;
  /** Dashboard slug, route path or URL — depending on `navigateType`. */
  navigateTarget: string;
  /** Screen (root frame) id when `navigateType` is `screen`. */
  navigateScreenId: string;
  navigateNewTab: boolean;
  /** Optional filter passed to the target dashboard. */
  navigateFilterKey: string;
  navigateFilterValue: string;
  /** Also send every filter selected on this screen (its dropdowns), e.g.
   *  customer name AND spec, so the next screen opens on the same selection. */
  navigateSendFilters: boolean;

  // ── Popup ──────────────────────────────────────────────────────────────────
  popupSource: ButtonPopupSource;
  /** Node id of a POPUP widget whose content is shown (source = widget). */
  popupWidgetId: string;
  /** Root frame id of the popup screen (source = screen). */
  popupScreenId: string;
  /**
   * Where the popup screen opens, as a share of the free space left of / above
   * it (0 = left / top edge, 1 = right / bottom edge), so the spot holds on any
   * window size. -1 = centred. Set by dragging the open popup in the builder.
   */
  popupPlaceX: number;
  popupPlaceY: number;
  popupTitle: string;
  popupMessage: string;
  /** Optional bold heading at the top of the box body. */
  popupHeading: string;
  popupHeadingSize: 'h1' | 'h2' | 'h3';
  popupSize: ButtonPopupSize;
  /** Side of the box in px when `popupSize` is `square`. */
  popupBoxSize: number;
  popupPosition: 'center' | 'drawer';
  popupCloseLabel: string;

  // ── Popup box style (inline content) ───────────────────────────────────────
  popupShowHeader: boolean;
  popupTextAlign: 'left' | 'center' | 'right';
  popupVerticalAlign: 'top' | 'middle' | 'bottom';
  popupFontSize: number;
  popupFontWeight: number;
  popupTextColor: string;
  popupBackgroundColor: string;
  popupHeaderBackground: string;
  popupHeaderTextColor: string;
  popupBorderColor: string;
  popupBorderWidth: number;
  popupBorderRadius: number;
  popupCloseBackground: string;
  popupCloseTextColor: string;
}

export const BUTTON_DEFAULTS: ButtonConfig = {
  action: 'none',

  label: 'Button',
  icon: '',
  iconPosition: 'left',
  labelAlign: 'center',
  fontFamily: 'Inter, Roboto, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  textColor: '#ffffff',
  backgroundColor: '#2970ff',
  hoverBackgroundColor: '#1f5bd6',
  borderColor: '#2970ff',
  borderWidth: 1,
  borderRadius: 8,
  fullWidth: true,
  confirmMessage: '',

  downloadSource: 'widget',
  downloadUrl: '',
  downloadMethod: 'GET',
  downloadBody: '',
  downloadWidgetId: '',
  downloadFormat: 'csv',
  downloadFileName: 'export',
  downloadEntity: '',
  downloadSheetName: 'Sheet1',

  apis: [],
  apiRunMode: 'sequential',
  approvalUrl: '',
  approvalSliderId: '',
  approvalUuidColumn: 'uuid',
  approvalValueColumn: '',
  approvalRows: 'edited',
  saveSource: 'table',
  saveTableId: '',
  saveUrl: '',
  saveEntity: '',
  saveBusinessKeys: [],
  saveColumns: [],
  saveSuccessMessage: 'Saved',
  approvalBody: '{\n  "uuid": "{recordId}",\n  "value": {value},\n  "userId": "{userEmail}"\n}',
  approvalSuccessMessage: 'Submitted for approval',
  approvalRefresh: true,
  approvalNavigate: false,
  apiTargets: [],
  apiSuccessMessage: 'Done',

  uploadUrl: '',
  uploadMode: 'json',
  uploadEntity: '',
  uploadKeyColumns: [],
  uploadBody: '{\n  "requestData": {\n    "operation": "upload",\n    "entityName": "{entity}",\n    "businessKeys": {businessKeys},\n    "fileName": "{fileName}",\n    "contentType": "{contentType}",\n    "fileBytes": "{fileBase64}"\n  }\n}',
  uploadFieldName: 'fileContents',
  uploadAccept: '.xlsx,.xls,.csv,.pdf',
  uploadMaxSizeMb: 10,
  uploadExtraFields: '',
  uploadSuccessMessage: 'Uploaded',
  uploadRefresh: false,

  exportWidgetId: '',
  exportUrl: EXCEL_EXPORT_DEFAULT_URL,
  exportBody: EXCEL_EXPORT_DEFAULT_BODY,
  exportFilterColumns: [],
  exportSheetName: 'Sheet1',
  exportFileName: '',

  filterTargets: [],
  filterField: '',
  filterValue: '',
  filterToggle: true,
  filterApiUrl: '',
  filterApiMethod: 'POST',
  filterApiBody: '',

  navigateType: 'dashboard',
  navigateTarget: '',
  navigateScreenId: '',
  navigateNewTab: false,
  navigateFilterKey: '',
  navigateFilterValue: '',
  navigateSendFilters: false,

  popupSource: 'screen',
  popupWidgetId: '',
  popupScreenId: '',
  popupPlaceX: -1,
  popupPlaceY: -1,
  popupTitle: 'Details',
  popupMessage: '',
  popupHeading: '',
  popupHeadingSize: 'h2',
  popupSize: 'medium',
  popupBoxSize: 360,
  popupPosition: 'center',
  popupCloseLabel: 'Close',

  popupShowHeader: true,
  popupTextAlign: 'left',
  popupVerticalAlign: 'top',
  popupFontSize: 14,
  popupFontWeight: 400,
  popupTextColor: '#1f2937',
  popupBackgroundColor: '#ffffff',
  popupHeaderBackground: '#f6f9fd',
  popupHeaderTextColor: '#24344d',
  popupBorderColor: '#e3eaf4',
  popupBorderWidth: 0,
  popupBorderRadius: 14,
  popupCloseBackground: '#ffffff',
  popupCloseTextColor: '#33415c',
};

export const BUTTON_ACTION_OPTIONS: Array<{ label: string; value: ButtonAction; icon: string }> = [
  { label: 'None (label only)', value: 'none', icon: 'smart_button' },
  { label: 'Download', value: 'download', icon: 'download' },
  { label: 'Upload', value: 'upload', icon: 'upload' },
  { label: 'Call API', value: 'api', icon: 'api' },
  { label: 'Filter', value: 'filter', icon: 'filter_alt' },
  { label: 'Navigate', value: 'navigate', icon: 'open_in_browser' },
  { label: 'Open popup', value: 'popup', icon: 'open_in_new' },
  { label: 'Close popup', value: 'closePopup', icon: 'close' },
  { label: 'Approval', value: 'approval', icon: 'verified' },
  { label: 'Export', value: 'export', icon: 'file_download' },
  { label: 'Save changes', value: 'saveTable', icon: 'save' },
];

/** The stored bag with every missing key filled from the defaults. */
export function buttonConfigOf(raw: Record<string, unknown> | null | undefined): ButtonConfig {
  const source = raw ?? {};
  const out = { ...BUTTON_DEFAULTS } as Record<string, unknown>;
  (Object.keys(BUTTON_DEFAULTS) as Array<keyof ButtonConfig>).forEach((key) => {
    const value = source[key];
    if (value !== undefined && value !== null) out[key] = value;
  });
  const config = out as unknown as ButtonConfig;
  config.apis = Array.isArray(config.apis) ? config.apis : [];
  config.apiTargets = stringList(config.apiTargets);
  config.filterTargets = stringList(config.filterTargets);
  config.saveBusinessKeys = stringList(config.saveBusinessKeys);
  config.saveColumns = Array.isArray(config.saveColumns)
    ? config.saveColumns
      .filter((entry): entry is ButtonSaveColumn => !!entry && typeof entry === 'object')
      .map((entry) => ({ column: String(entry.column ?? ''), sourceId: String(entry.sourceId ?? '') }))
    : [];
  config.uploadKeyColumns = stringList(config.uploadKeyColumns);
  return config;
}

/** The popup is a screen designed on the canvas — opened by the runtime, not the button. */
export function opensPopupScreen(config: Pick<ButtonConfig, 'action' | 'popupSource'>): boolean {
  return config.action === 'popup' && config.popupSource === 'screen';
}

/** The comma-separated target value(s), trimmed, empties dropped. */
export function filterValuesOf(config: Pick<ButtonConfig, 'filterValue'>): string[] {
  return String(config.filterValue ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function newButtonApiCall(index: number): ButtonApiCall {
  return {
    id: `api-${Date.now().toString(36)}-${index}`,
    name: `API ${index + 1}`,
    method: 'POST',
    url: '',
    body: '{\n  "componentId": "{componentId}"\n}',
    headers: '',
  };
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
}
